import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useHasAccess } from '@/hooks/useHasAccess';
import {
  CATALOGUE_PAGE_SIZE,
  worksheetPassApi,
  WorksheetPassApiError,
} from '@/services/worksheetPass';
import {
  CatalogueAnswersNames,
  CatalogueType,
  FieldUpsertInput,
  PreviewInput,
  WorksheetUpsertInput,
} from '@/types/worksheetPass';

const KEY = 'worksheet_pass';

export const worksheetPassKeys = {
  settings: [KEY, 'settings'] as const,
  catalogue: (name: string, type?: CatalogueType) =>
    [KEY, 'catalogue', name, type ?? 'all'] as const,
  catalogueName: (formRef: string) => [KEY, 'catalogue-name', formRef] as const,
  questions: (formRef: string) => [KEY, 'questions', formRef] as const,
  answers: (formRef: string, fieldRef: string) =>
    [KEY, 'answers', formRef, fieldRef] as const,
  fields: (formRef: string) => [KEY, 'fields', formRef] as const,
  styles: [KEY, 'styles'] as const,
  thresholds: [KEY, 'thresholds'] as const,
};

// A refusal (401/403) or bad input (400) won't change on a retry.
const retry = (failureCount: number, error: Error) => {
  if (
    error instanceof WorksheetPassApiError &&
    error.status !== null &&
    error.status < 500
  ) {
    return false;
  }
  return failureCount < 2;
};

export function useIsWorksheetPassAdmin() {
  return useHasAccess('edit_worksheet_pass');
}

export function useWorksheetPassSettings() {
  const isAdmin = useIsWorksheetPassAdmin();

  return useQuery({
    queryKey: worksheetPassKeys.settings,
    queryFn: worksheetPassApi.getSettings,
    enabled: isAdmin,
    retry,
  });
}

export function useWorksheetPassSwitch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: worksheetPassApi.setSwitch,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: worksheetPassKeys.settings }),
  });
}

export function useWorksheetPassThresholds() {
  const isAdmin = useIsWorksheetPassAdmin();

  return useQuery({
    queryKey: worksheetPassKeys.thresholds,
    queryFn: worksheetPassApi.getThresholds,
    enabled: isAdmin,
    retry,
  });
}

export function useSaveThresholds() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: worksheetPassApi.saveThresholds,
    onSuccess: saved => {
      queryClient.setQueryData(worksheetPassKeys.thresholds, saved);
      return queryClient.invalidateQueries({
        queryKey: worksheetPassKeys.settings,
      });
    },
  });
}

export function useCatalogueWorksheets(name: string, type?: CatalogueType) {
  const isAdmin = useIsWorksheetPassAdmin();

  return useInfiniteQuery({
    queryKey: worksheetPassKeys.catalogue(name, type),
    queryFn: ({ pageParam }) =>
      worksheetPassApi.getCatalogueWorksheets({
        name,
        type,
        pageNumber: pageParam,
      }),
    initialPageParam: 1,
    // BigChange gives no total, so a full page means there may be another.
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      (lastPage.items?.length ?? 0) >= CATALOGUE_PAGE_SIZE
        ? lastPageParam + 1
        : undefined,
    enabled: isAdmin,
    retry,
  });
}

// The catalogue has no id filter, so a name lookup reads big pages until it finds the id.
const NAME_LOOKUP_PAGE_SIZE = 500;
const NAME_LOOKUP_MAX_PAGES = 10;

async function findCatalogueWorksheetName(formRef: string) {
  for (let pageNumber = 1; pageNumber <= NAME_LOOKUP_MAX_PAGES; pageNumber++) {
    const page = await worksheetPassApi.getCatalogueWorksheets({
      pageNumber,
      pageSize: NAME_LOOKUP_PAGE_SIZE,
    });
    const items = page.items ?? [];
    const match = items.find(item => String(item.id) === formRef);
    if (match) return match.name?.trim() || null;
    if (items.length < NAME_LOOKUP_PAGE_SIZE) return null;
  }
  return null;
}

/** The worksheet's name in the catalogue, for a saved row that has none. */
export function useCatalogueWorksheetName(formRef: string, enabled: boolean) {
  const isAdmin = useIsWorksheetPassAdmin();

  return useQuery({
    queryKey: worksheetPassKeys.catalogueName(formRef),
    queryFn: () => findCatalogueWorksheetName(formRef),
    enabled: enabled && isAdmin && !!formRef,
    staleTime: 10 * 60 * 1000,
    retry,
  });
}

export function useCatalogueQuestions(formRef: string) {
  return useQuery({
    queryKey: worksheetPassKeys.questions(formRef),
    queryFn: () => worksheetPassApi.getCatalogueQuestions(formRef),
    enabled: !!formRef,
    retry,
  });
}

export function useCatalogueAnswers(
  formRef: string,
  fieldRef: string,
  enabled: boolean,
  names?: CatalogueAnswersNames
) {
  return useQuery({
    queryKey: worksheetPassKeys.answers(formRef, fieldRef),
    queryFn: () =>
      worksheetPassApi.getCatalogueAnswers(formRef, fieldRef, names),
    enabled: enabled && !!formRef && !!fieldRef,
    staleTime: 5 * 60 * 1000,
    retry,
  });
}

export function useWorksheetFields(formRef: string, enabled = true) {
  return useQuery({
    queryKey: worksheetPassKeys.fields(formRef),
    queryFn: () => worksheetPassApi.getWorksheetFields(formRef),
    enabled: enabled && !!formRef,
    retry,
  });
}

export function useWorksheetPassStyles() {
  return useQuery({
    queryKey: worksheetPassKeys.styles,
    queryFn: worksheetPassApi.getStyles,
    staleTime: 10 * 60 * 1000,
    retry,
  });
}

export function useUpsertWorksheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: WorksheetUpsertInput) =>
      worksheetPassApi.upsertWorksheet(input),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: worksheetPassKeys.settings });
      // A changed note marks the worksheet's fields untested, and `fields` seeds rows.
      queryClient.invalidateQueries({
        queryKey: worksheetPassKeys.fields(input.form_ref),
      });
    },
  });
}

export function useUpsertField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: FieldUpsertInput) =>
      worksheetPassApi.upsertField(input),
    onSuccess: data => {
      const formRefs = new Set([
        data.field.form_ref,
        ...data.applied_to.map(item => item.form_ref),
      ]);
      formRefs.forEach(formRef =>
        queryClient.invalidateQueries({
          queryKey: worksheetPassKeys.fields(formRef),
        })
      );
      // The save settled a VH3 suggestion (accepted or edited): it is no longer open.
      if (data.suggestion_status) {
        queryClient.invalidateQueries({ queryKey: worksheetPassKeys.styles });
      }
    },
  });
}

export function useDismissSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formRef,
      fieldRef,
    }: {
      formRef: string;
      fieldRef: string;
    }) => worksheetPassApi.dismissSuggestion(formRef, fieldRef),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: worksheetPassKeys.styles }),
  });
}

export function usePreview() {
  return useMutation({
    mutationFn: (input: PreviewInput) => worksheetPassApi.preview(input),
  });
}
